<template>
  <div class="mx-3" style="page-break-inside: avoid; padding-bottom: 1em">
    <hr style="margin-top: 2em">
      <div class="row">
        <div class="col-9">
            <h2 class="pl-1" style="font-size: 1.2em; font-weight: bold">{{ getTitle(assignment) }}</h2>
        </div>
        <div class="col-3 d-flex justify-content-end">
            <button class="btn btn-sm btn-danger" style="height: 2.5em" @click="$emit('remove', assignment.id)">
                {{ $t('observations.remove_from_report')}}
            </button>
        </div>
    </div>
    <div
      v-if="photos.length > 1 && showAllPhotos"
      class="row nopads"
    >
      <div
        v-for="(photo, index) in photos"
        :key="index"
        class="col-6 pb-2 multi-img-row"
      >
        <div class="image-container">
          <ImageView
            :img-size="'sm'"
            :url="photo.url"
            :base64data="photo.src"
            control-size="sm"
            :cache-photo="true"
            :cacheable-photo-id="assignment.id + '_' + index"
            :contain="true"
            :height="'300px'"
            :control-size="'sm'"
            @image-loaded="onImageLoaded()"
          />
        </div>
      </div>
    </div>
    <div
      v-else-if="photos.length > 0"
      class="nopads text-center"
    >
      <div class="col-12 nopads">
        <div class="image-container">
          <ImageView
            :img-size="'sm'"
            :url="photos[0].url"
            :base64data="photos[0].src"
            control-size="sm"
            :cache-photo="true"
            :cacheable-photo-id="assignment.id + '_0'"
            :contain="true"
            :height="'420px'"
            :control-size="'sm'"
            @image-loaded="onImageLoaded()"
          />
        </div>
      </div>
    </div>
      <div v-if="assignment.geometry || assignment.observation" class="my-3">
          <!-- Fixed width div -->
          <div style="height: 300px; width: 730px; position: relative;">
              <marker-editor-map
                  v-if="assignment"
                  :id="assignment.id"
                  ref="markerEditorMap"
                  :lat="latitude"
                  :lon="longitude"
                  :type="WORK_ASSIGNMENT"
                  :draggable="false"
                  @mounted="mapMounted"
                  style="height: 300px !important; width: 730px !important"
              />
          </div>
      </div>
      <!-- Flexible width div -->
      <div class="pt-4">
          <table class="table table-sm">
              <thead>
              <tr>
                  <th>{{ $t('work_assignment.id') }}</th>
                  <th>{{ $t('work_assignment.task') }}</th>
                  <th>{{ $t('orders.status') }}</th>
                  <th>{{ $t('area_report_details.info') }}</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                  <td>{{ assignment.id }}</td>
                  <td>{{ getTitle(assignment)}}</td>
                  <td>{{ getStatusString(assignment.status) }}</td>
                  <td>{{ getAssignmentInfo(assignment) }}</td>
              </tr>
              </tbody>
          </table>
      </div>
      <!-- Comments not shown for now, but we get photos from them -->
      <comment-container
          ref="comments"
          v-if="assignment && assignment.id > 0"
          class="hidden"
          :topic="commentTopics.TOPIC_WORK_ASSIGNMENT"
          :id="assignment.id"
          :custom-api-url="workAssignmentCommentUrl"
          :user="user"
          @commentsReady="initPhotos"
      />
  </div>
</template>

<script>
import {timeUtils} from '../mixins/TimeUtils'
import ImageView from '../view/ImageView'
import {restApi} from '../mixins/RestApiMixin'
import MarkerEditorMap from '../map/MarkerEditorMap'
import {mapHelper} from '../mixins/MapMixin'
import {Pagination} from "@egjs/flicking-plugins";
import {workAssignmentHelper} from "../mixins/WorkAssignmentMixin.js";
import CommentContainer from "../comments/CommentContainer.vue";
import {commentTopics} from "../mixins/CommentMixin.js";

export default {
    name: 'WorkAssignmentPrintDetails',
    components: {CommentContainer, MarkerEditorMap, ImageView},
    mixins: [timeUtils, restApi, mapHelper, workAssignmentHelper],
    props: {
        assignment: {
            type: Object,
            default() {
                return null
            }
        },
        reportCompleted: {
            type: Boolean,
            default() {
                return false
            }
        },
        showAllPhotos: {
            type: Boolean,
            default: true
        },
        user: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            mapReady: false,
            imgLoaded: 0,
            plugins: [new Pagination({type: 'bullet'})],
            options: {
                preventDefaultOnDrag: true,
                bound: true,
            },
            photos: []
        }
    },
    computed: {
        latitude() {
            return this.getWorkAssignmentPointPosition(this.assignment).y
        },
        longitude() {
            return this.getWorkAssignmentPointPosition(this.assignment).x
        },
        commentTopics() {
            return commentTopics
        },
    },
    watch: {
        reportCompleted(val) {
            if (val && this.mapReady) {
                this.switchMapToImage()
            }
        },
        imgLoaded: function() {
            if (this.imgLoaded === this.photos.length) {
                this.$emit('loadingCompleted')
            }
        }
    },
    created() {
        if (this.photos && this.photos.length > 0) {
            this.$emit('loadingStarted')
        } else if (!this.photos || this.photos.length < 1) {
            this.$emit('loadingCompleted')
        }
    },
    methods: {
        initPhotos() {
            const commentPhotos = this.$refs.comments?.getPhotos()
            this.photos = this.getAssignmentPhotos(this.assignment, commentPhotos )
        },

        getMarkerEditorMap() {
            return this.map?.getMap()
        },
        mapMounted(map) {
            this.map = map
            this.mapReady = true
            if (this.reportCompleted) {
                // No photos in report, give time for map to load
                setTimeout(() => this.switchMapToImage(), 3000 )
            }
            this.$nextTick(() => this.map.hideMapControls())
        },
        switchMapToImage() {
                this.$emit('readyToPrint')
        },
        onImageLoaded() {
            this.imgLoaded++
        },
        getPhotos() {
            return this.photos
        }
    }
}
</script>

<style scoped>

.detail-map, .report-photos {
    display: block;
    height: 250px;
    width: 450px;
    overflow: hidden;
}

.detail-map {
    position: absolute;
    border-radius: 1.2em;
    border: 1px solid #BCBCBC;
}

.report-photos {
    margin-top: 210px;
}
.image-container {
    width: fit-content;
    margin: auto;
}
</style>

